import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Fab,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Rnd } from "react-rnd";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import { sendChatMessage, getChatHistory, clearChatHistory } from "../../Redux/Actions/Chat";
import ChatLogo from "../../Asssets/Images/chat_logo.png";

/** Above everything: MUI modals (1300), dashboard sticky headers (99999), toasts (999999). */
const CHAT_Z_INDEX = 2000000;

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function getFirstName(fullName) {
  if (!fullName) return "";
  return fullName.split(" ")[0];
}

function getSuggestions(role) {
  const common = [
    "What's my attendance this month?",
    "How many leaves do I have left?",
    "Show upcoming holidays",
  ];

  const managerSuggestions = [
    "Show my team's attendance today",
    "How many leaves did my team take?",
    "Any attrition risks in my team?",
  ];

  const hrAdminSuggestions = [
    "Show today's attendance summary",
    "How many tickets were generated this month?",
    "Forecast next month's payroll",
    "Show hiring forecast",
  ];

  switch (role) {
    case "HR":
    case "admin":
      return hrAdminSuggestions;
    case "manager":
      return managerSuggestions;
    default:
      return common;
  }
}

const AiChat = () => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { messages, loading, historyLoaded } = useSelector((state) => state.chat);
  const { user: currentUserData } = useSelector((state) => state.currentUser);
  const employee = currentUserData?.user;
  const messagesEndRef = useRef(null);
  const prevUserIdRef = useRef(null);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const isAuthenticated = Boolean(employee?._id);

  const [panelSize, setPanelSize] = useState({ width: 400, height: 560 });

  const panelPosition = useMemo(() => {
    // Anchor bottom-right with the same margin as the previous fixed positioning.
    const margin = 24;
    const width = typeof panelSize.width === "number" ? panelSize.width : Number(panelSize.width);
    const height = typeof panelSize.height === "number" ? panelSize.height : Number(panelSize.height);
    const x = Math.max(margin, window.innerWidth - width - margin);
    const y = Math.max(margin, window.innerHeight - height - margin);
    return { x, y };
  }, [panelSize.height, panelSize.width]);

  useEffect(() => {
    if (!employee?._id) return;

    if (prevUserIdRef.current && prevUserIdRef.current !== employee._id.toString()) {
      dispatch({ type: "CHAT_USER_SWITCH" });
      setOpen(false);
    }
    prevUserIdRef.current = employee._id.toString();
  }, [employee?._id, dispatch]);

  useEffect(() => {
    if (open && !historyLoaded && isAuthenticated) {
      dispatch(getChatHistory());
    }
  }, [open, historyLoaded, dispatch, isAuthenticated]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  useEffect(() => {
    if (!open) return;
    // When opening, ensure we land on the latest messages (also after history finishes loading).
    const t = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "auto" });
      }
    }, 0);
    return () => clearTimeout(t);
  }, [open, historyLoaded]);

  const handleSend = (message) => {
    dispatch(sendChatMessage(message));
  };

  const handleClear = () => {
    dispatch(clearChatHistory());
  };

  const showPolicyCta = useMemo(() => {
    const role = employee?.role;
    return role === "employee" || role === "manager";
  }, [employee?.role]);

  const handlePolicyCta = () => {
    handleSend("Show the company policies for my department.");
  };

  if (!isAuthenticated) return null;

  if (!open) {
    return (
      <Tooltip
        title="HR Buddy"
        placement="left"
        PopperProps={{ sx: { zIndex: CHAT_Z_INDEX + 1 } }}
      >
        <Fab
          onClick={() => setOpen(true)}
          disableRipple
          disableFocusRipple
          aria-label="Open HR Buddy"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: CHAT_Z_INDEX,
            width: 72,
            height: 72,
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible",
            p: 0,
            "&:hover": {
              backgroundColor: "transparent",
              transform: "scale(1.1)",
            },
            "&:active": {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
            "&.Mui-focusVisible": {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
            "& .MuiTouchRipple-root": {
              display: "none",
            },
            transition: "transform 0.2s ease",
          }}
        >
          <img src={ChatLogo} alt="HR Buddy" style={{ width: 72, height: 72, objectFit: "contain" }} />
        </Fab>
      </Tooltip>
    );
  }

  const ChatPanel = (
    <Paper
      elevation={8}
      sx={{
        width: "100%",
        height: "100%",
        zIndex: CHAT_Z_INDEX,
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          color: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
            }}
          >
            <img
              src={ChatLogo}
              alt="HR Buddy"
              style={{ width: 32, height: 32, objectFit: "contain" }}
            />
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            HR Buddy
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Clear chat" PopperProps={{ sx: { zIndex: CHAT_Z_INDEX + 1 } }}>
            <IconButton size="small" onClick={handleClear} sx={{ color: "rgba(255,255,255,0.8)" }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          py: 1.5,
          backgroundColor: "#fafbfc",
        }}
      >
        {messages.length === 0 && !loading && (
          <Box sx={{ px: 1, pt: 2 }}>
            <Box sx={{ textAlign: "center", mb: 2.5 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <img
                  src={ChatLogo}
                  alt="HR Buddy"
                  style={{ width: 64, height: 64, objectFit: "contain", background: "transparent" }}
                />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                {getGreeting()}{employee?.full_name ? `, ${getFirstName(employee.full_name)}` : ""}!
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                I'm your HR Buddy. Here are some things I can help you with:
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {showPolicyCta && (
                <Chip
                  key="policy-cta"
                  label="View company policies"
                  variant="outlined"
                  onClick={handlePolicyCta}
                  sx={{
                    height: "auto",
                    py: 1,
                    px: 0.5,
                    fontSize: "0.8rem",
                    borderColor: "#e0e0e0",
                    color: "#444",
                    cursor: "pointer",
                    whiteSpace: "normal",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      lineHeight: 1.4,
                    },
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                      borderColor: "#1976d2",
                      color: "#1976d2",
                    },
                  }}
                />
              )}
              {getSuggestions(employee?.role).map((suggestion) => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  variant="outlined"
                  onClick={() => handleSend(suggestion)}
                  sx={{
                    height: "auto",
                    py: 1,
                    px: 0.5,
                    fontSize: "0.8rem",
                    borderColor: "#e0e0e0",
                    color: "#444",
                    cursor: "pointer",
                    whiteSpace: "normal",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      lineHeight: 1.4,
                    },
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                      borderColor: "#1976d2",
                      color: "#1976d2",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        {messages.map((msg) => (
          <ChatMessage
            key={`${msg.role || "unknown"}-${msg.timestamp || "no-ts"}-${String(msg.content || "").slice(0, 24)}`}
            message={msg}
          />
        ))}
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary">
              Thinking...
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <ChatInput onSend={handleSend} disabled={loading} />
    </Paper>
  );

  if (isSmall) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          width: "calc(100vw - 32px)",
          height: "calc(100vh - 100px)",
          zIndex: CHAT_Z_INDEX,
        }}
      >
        {ChatPanel}
      </Box>
    );
  }

  return (
    <Rnd
      size={panelSize}
      position={panelPosition}
      onResizeStop={(e, direction, ref) => {
        setPanelSize({ width: ref.offsetWidth, height: ref.offsetHeight });
      }}
      enableResizing={{
        top: true,
        right: true,
        bottom: true,
        left: true,
        topRight: true,
        bottomRight: true,
        bottomLeft: true,
        topLeft: true,
      }}
      disableDragging
      bounds="window"
      minWidth={320}
      minHeight={420}
      maxWidth={1100}
      maxHeight={760}
      style={{ zIndex: CHAT_Z_INDEX, position: "fixed" }}
    >
      {ChatPanel}
    </Rnd>
  );
};

export default AiChat;
